/* BEGIN-RN-HEADER-ENHANCEMENTS */

/**
 * Rage Nation Header Enhancements
 * JavaScript orchestration for animations, sticky behaviors, and interactions
 * Requires: Motion One (already available in theme)
 */

class RnHeaderEnhancements {
  constructor(sectionElement) {
    this.section = sectionElement;
    this.header = sectionElement.querySelector('[data-rn-header]');
    this.abortController = new AbortController();
    this.state = {
      lastScrollY: window.scrollY,
      isScrollingDown: false,
      isSticky: false,
      isCompressed: false,
      isActivelyScrolling: false,
      scrollTimeout: null,
    };

    if (!this.header) return;
    this.init();
  }

  init() {
    this.initStickyBehavior();
    this.initMagneticLinks();
    this.initCartFeedback();
    this.initDarkMode();
    this.initMegaMenuEnhancements();
    this.initDropdownHoverIntent();
    this.initCollisionDetection();

    // Emit initialization event
    document.dispatchEvent(
      new CustomEvent('rn:header:init', {
        detail: { element: this.header },
      })
    );
  }

  /**
   * Sticky compression & smart hide/reveal
   */
  initStickyBehavior() {
    if (!this.header.dataset.rnCompress && !this.header.dataset.rnReveal) {
      return;
    }

    let ticking = false;
    const threshold = parseInt(this.header.dataset.rnRevealThreshold) || 100;

    // Hysteresis thresholds to prevent flickering when hovering around threshold
    // INCREASED GAP: 70px - 25px = 45px buffer to prevent slow-scroll flickering
    const COMPRESS_THRESHOLD = 70;
    const UNCOMPRESS_THRESHOLD = 25;

    const updateStickyState = () => {
      const scrollY = window.scrollY;
      const scrollDelta = Math.abs(scrollY - this.state.lastScrollY);

      // Compression logic with hysteresis
      if (this.header.dataset.rnCompress === 'true') {
        let shouldCompress = this.state.isCompressed;

        // Add hysteresis: different thresholds for compressing vs uncompressing
        // Also require minimum scroll movement to prevent micro-adjustments
        if (!this.state.isCompressed && scrollY > COMPRESS_THRESHOLD) {
          shouldCompress = true;
        } else if (this.state.isCompressed && scrollY < UNCOMPRESS_THRESHOLD) {
          shouldCompress = false;
        }

        // Only apply compression change if scroll delta is meaningful (> 2px)
        // This prevents flickering from tiny scroll movements
        if (shouldCompress !== this.state.isCompressed && scrollDelta > 2) {
          this.header.classList.toggle('rn-header--compressed', shouldCompress);
          this.state.isCompressed = shouldCompress;
        }
      }

      // Smart hide/reveal logic
      if (this.header.dataset.rnReveal === 'true') {
        this.state.isScrollingDown = scrollY > this.state.lastScrollY;

        if (this.state.isScrollingDown && scrollY > threshold) {
          this.header.classList.add('rn-header--hidden');
        } else if (!this.state.isScrollingDown || scrollY <= threshold) {
          this.header.classList.remove('rn-header--hidden');
        }
      }

      this.state.lastScrollY = scrollY;

      // Track active scrolling state
      this.state.isActivelyScrolling = true;
      clearTimeout(this.state.scrollTimeout);
      this.state.scrollTimeout = setTimeout(() => {
        this.state.isActivelyScrolling = false;
      }, 150);

      ticking = false;

      // Emit sticky state change event
      document.dispatchEvent(
        new CustomEvent('rn:header:sticky:change', {
          detail: {
            compressed: this.state.isCompressed,
            hidden: this.header.classList.contains('rn-header--hidden'),
            scrollY,
            isActivelyScrolling: this.state.isActivelyScrolling,
          },
        })
      );
    };

    const scrollHandler = () => {
      if (!ticking) {
        requestAnimationFrame(updateStickyState);
        ticking = true;
      }
    };

    window.addEventListener('scroll', scrollHandler, {
      passive: true,
      signal: this.abortController.signal,
    });

    // Initial check
    updateStickyState();
  }

  /**
   * Magnetic hover effect for navigation links
   * Only active on pointer: fine devices
   */
  initMagneticLinks() {
    if (this.header.dataset.rnHover !== 'magnetic') {
      return;
    }

    // Check if device has fine pointer (mouse)
    if (!window.matchMedia('(pointer: fine)').matches) {
      return;
    }

    const navItems = this.header.querySelectorAll(
      '.header__primary-nav-item > a, .header__primary-nav-item > details > summary'
    );
    const magneticDistance =
      parseFloat(getComputedStyle(this.header).getPropertyValue('--rn-nav-magnetic-distance')) || 4;

    navItems.forEach((item) => {
      const handleMouseMove = (e) => {
        const rect = item.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;

        // Calculate distance from center
        const deltaX = (e.clientX - centerX) / rect.width;
        const deltaY = (e.clientY - centerY) / rect.height;

        // Apply magnetic effect
        const x = deltaX * magneticDistance;
        const y = deltaY * magneticDistance;

        item.style.setProperty('--rn-magnetic-x', `${x}px`);
        item.style.setProperty('--rn-magnetic-y', `${y}px`);
      };

      const handleMouseLeave = () => {
        item.style.setProperty('--rn-magnetic-x', '0');
        item.style.setProperty('--rn-magnetic-y', '0');
      };

      item.addEventListener('mousemove', handleMouseMove, {
        signal: this.abortController.signal,
      });

      item.addEventListener('mouseleave', handleMouseLeave, {
        signal: this.abortController.signal,
      });
    });
  }

  /**
   * Cart icon bounce animation on add-to-cart
   */
  initCartFeedback() {
    const cartIcon = this.header.querySelector('[data-rn-cart]');
    if (!cartIcon) return;

    const feedbackType = cartIcon.dataset.rnCartFeedback;
    if (!feedbackType || feedbackType === 'none') return;

    const handleCartChange = () => {
      // Check for reduced motion preference
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return;
      }

      // Add animation class
      cartIcon.classList.add('rn-cart--animating');

      // Remove class after animation completes
      const duration = parseFloat(getComputedStyle(cartIcon).getPropertyValue('--rn-cart-bounce-duration')) || 0.4;
      setTimeout(() => {
        cartIcon.classList.remove('rn-cart--animating');
      }, duration * 1000);

      // Emit cart animation event
      document.dispatchEvent(
        new CustomEvent('rn:cart:animate', {
          detail: { type: feedbackType },
        })
      );
    };

    document.addEventListener('cart:change', handleCartChange, {
      signal: this.abortController.signal,
    });
  }

  /**
   * Dark mode auto-detection
   */
  initDarkMode() {
    if (this.header.dataset.rnDarkAuto !== 'true') {
      return;
    }

    const darkModeQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const updateDarkMode = () => {
      this.header.dataset.rnDark = darkModeQuery.matches ? 'true' : 'false';
    };

    updateDarkMode();

    darkModeQuery.addEventListener('change', updateDarkMode, {
      signal: this.abortController.signal,
    });
  }

  /**
   * Enhanced hover intent for dropdowns
   * Prevents premature closing when moving cursor diagonally from trigger to panel
   */
  initDropdownHoverIntent() {
    // Only apply on devices with fine pointer control (mouse)
    if (!window.matchMedia('(pointer: fine)').matches) {
      return;
    }

    const disclosures = this.section.querySelectorAll('.header__menu-disclosure[trigger="hover"]');

    disclosures.forEach((disclosure) => {
      let hoverIntent = {
        timeout: null,
        lastMousePos: { x: 0, y: 0 },
        tolerance: 300, // Increased delay for better diagonal movement
        isMovingTowardsMenu: false,
      };

      const getMenuBounds = () => {
        const menu = disclosure.querySelector('.header__dropdown-menu, .mega-menu');
        if (!menu) return null;
        return menu.getBoundingClientRect();
      };

      const isMovingTowardsMenu = (event) => {
        const menuBounds = getMenuBounds();
        if (!menuBounds || !disclosure.open) return false;

        const currentPos = { x: event.clientX, y: event.clientY };
        const lastPos = hoverIntent.lastMousePos;

        // Calculate if cursor is moving towards menu area
        // Create a triangular "safe zone" from last position towards menu
        const menuCenterX = menuBounds.left + menuBounds.width / 2;
        const menuCenterY = menuBounds.top + menuBounds.height / 2;

        // Vector from last to current position
        const moveVectorX = currentPos.x - lastPos.x;
        const moveVectorY = currentPos.y - lastPos.y;

        // Vector from last position to menu center
        const toMenuX = menuCenterX - lastPos.x;
        const toMenuY = menuCenterY - lastPos.y;

        // Dot product indicates if moving towards menu
        const dotProduct = moveVectorX * toMenuX + moveVectorY * toMenuY;

        // Update last position for next calculation
        hoverIntent.lastMousePos = currentPos;

        // If dot product is positive and cursor within reasonable range, moving towards menu
        return (
          dotProduct > 0 &&
          currentPos.x >= menuBounds.left - 50 &&
          currentPos.x <= menuBounds.right + 50 &&
          currentPos.y >= menuBounds.top - 50 &&
          currentPos.y <= menuBounds.bottom + 50
        );
      };

      // Enhanced mouseover handler
      const handleMouseOver = (event) => {
        clearTimeout(hoverIntent.timeout);
        hoverIntent.lastMousePos = { x: event.clientX, y: event.clientY };
      };

      // Enhanced mouseout handler with intent detection
      const handleMouseOut = (event) => {
        clearTimeout(hoverIntent.timeout);

        // Check if moving towards menu
        if (isMovingTowardsMenu(event)) {
          hoverIntent.isMovingTowardsMenu = true;
          // Extend the tolerance when moving towards menu
          hoverIntent.timeout = setTimeout(() => {
            hoverIntent.isMovingTowardsMenu = false;
          }, hoverIntent.tolerance + 150);
          return;
        }

        // Standard close delay
        hoverIntent.timeout = setTimeout(() => {
          // Don't close if we detected movement towards menu recently
          if (!hoverIntent.isMovingTowardsMenu) {
            disclosure.open = false;
          }
        }, hoverIntent.tolerance);
      };

      // Track mouse movement for better intent detection
      const handleMouseMove = (event) => {
        hoverIntent.lastMousePos = { x: event.clientX, y: event.clientY };
      };

      // Apply enhanced handlers
      disclosure.addEventListener('mouseover', handleMouseOver, {
        signal: this.abortController.signal,
      });

      disclosure.addEventListener('mouseout', handleMouseOut, {
        signal: this.abortController.signal,
      });

      disclosure.addEventListener('mousemove', handleMouseMove, {
        passive: true,
        signal: this.abortController.signal,
      });

      // Emit hover intent initialization
      disclosure.dataset.rnHoverIntent = 'true';
    });

    // Emit event for debugging/monitoring
    document.dispatchEvent(
      new CustomEvent('rn:dropdown:hover-intent:init', {
        detail: { count: disclosures.length },
      })
    );
  }

  /**
   * Mega menu entrance animations and enhancements
   */
  initMegaMenuEnhancements() {
    const megaMenus = this.section.querySelectorAll('[data-rn-mega]');

    megaMenus.forEach((mega) => {
      // Find the disclosure element that controls this mega menu
      const disclosure = mega.closest('details');
      if (!disclosure) return;

      // Listen for menu open/close
      const handleToggle = () => {
        if (disclosure.open) {
          // Emit mega menu open event
          document.dispatchEvent(
            new CustomEvent('rn:mega:open', {
              detail: {
                menuId: mega.id || 'unknown',
                layout: mega.dataset.rnMegaLayout,
                style: mega.dataset.rnStyle,
              },
            })
          );
        }
      };

      disclosure.addEventListener('toggle', handleToggle, {
        signal: this.abortController.signal,
      });
    });
  }

  /**
   * Collision-aware positioning for dropdowns and mega menus
   * Dynamically adapts menu position to prevent viewport clipping
   */
  initCollisionDetection() {
    const disclosures = this.section.querySelectorAll('.header__menu-disclosure');

    // Configuration from CSS variables or defaults
    const getConfig = () => ({
      safePadding: parseInt(getComputedStyle(this.header).getPropertyValue('--rn-collision-safe-padding')) || 16,
      maxWidthDesktop: parseInt(getComputedStyle(this.header).getPropertyValue('--rn-dropdown-max-width')) || 1200,
      maxHeight: parseInt(getComputedStyle(this.header).getPropertyValue('--rn-dropdown-max-height')) || 600,
    });

    const getStickyOffset = () => {
      // Calculate total offset from sticky header and admin bar
      let offset = 0;
      const headerRect = this.section.getBoundingClientRect();
      if (headerRect.top >= 0) {
        offset = headerRect.bottom;
      }
      // Check for Shopify admin bar
      const adminBar = document.getElementById('admin-bar-iframe');
      if (adminBar) {
        offset += adminBar.offsetHeight || 0;
      }
      return offset;
    };

    const getAvailableSpace = (triggerRect) => {
      const config = getConfig();
      const stickyOffset = getStickyOffset();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      return {
        top: triggerRect.top - stickyOffset - config.safePadding,
        bottom: viewportHeight - triggerRect.bottom - config.safePadding,
        left: triggerRect.left - config.safePadding,
        right: viewportWidth - triggerRect.right - config.safePadding,
      };
    };

    const isRTL = () => document.dir === 'rtl' || document.documentElement.dir === 'rtl';

    const positionDropdown = (disclosure, menu, isMegaMenu) => {
      const trigger = disclosure.querySelector('summary');
      if (!trigger) return;

      const config = getConfig();
      const triggerRect = trigger.getBoundingClientRect();
      const available = getAvailableSpace(triggerRect);

      // Reset positioning classes and inline styles
      menu.classList.remove('rn-flipped-h', 'rn-flipped-v', 'rn-constrained-width', 'rn-constrained-height');
      menu.style.removeProperty('max-width');
      menu.style.removeProperty('max-height');
      menu.style.removeProperty('left');
      menu.style.removeProperty('right');
      menu.style.removeProperty('transform');
      menu.style.removeProperty('transform-origin');

      // Get menu dimensions (temporarily show to measure)
      const wasVisible = menu.style.visibility;
      menu.style.visibility = 'hidden';
      menu.style.display = 'block';
      const menuRect = menu.getBoundingClientRect();
      menu.style.visibility = wasVisible;
      menu.style.display = '';

      let adjustedLeft = 0;
      let adjustedWidth = menuRect.width;
      let adjustedHeight = menuRect.height;
      let transformOriginX = 'left';
      let transformOriginY = 'top';

      // Horizontal positioning
      if (isMegaMenu) {
        // Mega menus try to center by default
        const menuWidth = Math.min(menuRect.width, config.maxWidthDesktop, window.innerWidth - config.safePadding * 2);
        const idealLeft = triggerRect.left + triggerRect.width / 2 - menuWidth / 2;

        if (idealLeft < config.safePadding) {
          // Too far left, align to safe padding
          adjustedLeft = config.safePadding;
          transformOriginX = 'left';
        } else if (idealLeft + menuWidth > window.innerWidth - config.safePadding) {
          // Too far right, align to right safe padding
          adjustedLeft = window.innerWidth - menuWidth - config.safePadding;
          transformOriginX = 'right';
        } else {
          // Fits centered
          adjustedLeft = idealLeft;
          transformOriginX = 'center';
        }

        menu.style.left = `${adjustedLeft}px`;
        menu.style.transform = 'none';
        adjustedWidth = menuWidth;

        if (adjustedWidth < menuRect.width) {
          menu.style.maxWidth = `${adjustedWidth}px`;
          menu.classList.add('rn-constrained-width');
        }
      } else {
        // Regular dropdowns align to trigger by default
        const rtl = isRTL();

        if (rtl) {
          // RTL: Align right edge of menu to right edge of trigger
          const idealRight = window.innerWidth - triggerRect.right;

          if (triggerRect.right - menuRect.width < config.safePadding) {
            // Would overflow left, flip to left-align
            menu.style.left = `${config.safePadding}px`;
            menu.style.right = 'auto';
            menu.classList.add('rn-flipped-h');
            transformOriginX = 'left';
          } else {
            menu.style.right = `${idealRight}px`;
            menu.style.left = 'auto';
            transformOriginX = 'right';
          }
        } else {
          // LTR: Align left edge of menu to left edge of trigger
          if (triggerRect.left + menuRect.width > window.innerWidth - config.safePadding) {
            // Would overflow right, flip to right-align
            menu.style.right = '0';
            menu.style.left = 'auto';
            menu.classList.add('rn-flipped-h');
            transformOriginX = 'right';
          } else {
            menu.style.left = '0';
            menu.style.right = 'auto';
            transformOriginX = 'left';
          }
        }

        // Check if menu still overflows after flip
        const tempRect = menu.getBoundingClientRect();
        if (tempRect.width > window.innerWidth - config.safePadding * 2) {
          adjustedWidth = window.innerWidth - config.safePadding * 2;
          menu.style.maxWidth = `${adjustedWidth}px`;
          menu.classList.add('rn-constrained-width');
        }
      }

      // Vertical positioning - check available space below
      const maxAllowedHeight = Math.min(config.maxHeight, available.bottom);

      if (menuRect.height > maxAllowedHeight) {
        adjustedHeight = maxAllowedHeight;
        menu.style.maxHeight = `${adjustedHeight}px`;
        menu.classList.add('rn-constrained-height');

        // Enable internal scrolling
        menu.style.overflowY = 'auto';
        menu.style.overscrollBehavior = 'contain';
      }

      // Set transform origin for smooth animations
      menu.style.transformOrigin = `${transformOriginX} ${transformOriginY}`;

      // Set data attributes for styling hooks
      disclosure.dataset.rnPosition = transformOriginX;

      // Emit positioning event for debugging/monitoring
      document.dispatchEvent(
        new CustomEvent('rn:dropdown:positioned', {
          detail: {
            disclosure,
            menu,
            isMegaMenu,
            adjustments: {
              width: adjustedWidth !== menuRect.width,
              height: adjustedHeight !== menuRect.height,
              horizontalFlip: menu.classList.contains('rn-flipped-h'),
            },
            available,
          },
        })
      );
    };

    const handleDisclosureOpen = (disclosure) => {
      const menu = disclosure.querySelector('.header__dropdown-menu, .mega-menu');
      if (!menu) return;

      const isMegaMenu = menu.classList.contains('mega-menu');

      // Position on open
      positionDropdown(disclosure, menu, isMegaMenu);
    };

    // Attach toggle listeners to all disclosures
    disclosures.forEach((disclosure) => {
      const handleToggle = () => {
        if (disclosure.open) {
          handleDisclosureOpen(disclosure);
        }
      };

      disclosure.addEventListener('toggle', handleToggle, {
        signal: this.abortController.signal,
      });
    });

    // Reposition on resize/zoom with debounce
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        // Reposition all open menus
        disclosures.forEach((disclosure) => {
          if (disclosure.open) {
            handleDisclosureOpen(disclosure);
          }
        });
      }, 150);
    };

    window.addEventListener('resize', handleResize, {
      passive: true,
      signal: this.abortController.signal,
    });

    // Reposition on scroll (for sticky header height changes)
    // Only reposition when scrolling has stopped to prevent layout thrashing
    let scrollTimeout;
    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        // Only reposition if not actively scrolling
        if (!this.state.isActivelyScrolling) {
          disclosures.forEach((disclosure) => {
            if (disclosure.open) {
              handleDisclosureOpen(disclosure);
            }
          });
        }
      }, 200); // Increased debounce for better performance
    };

    window.addEventListener('scroll', handleScroll, {
      passive: true,
      signal: this.abortController.signal,
    });

    // Emit collision detection initialization
    document.dispatchEvent(
      new CustomEvent('rn:collision:init', {
        detail: { count: disclosures.length },
      })
    );
  }

  /**
   * Cleanup - remove all event listeners
   */
  destroy() {
    this.abortController.abort();
  }
}

/**
 * Global instance manager
 */
if (!window.RnHeaderEnhancements) {
  window.RnHeaderEnhancements = {
    instances: new WeakMap(),

    init(sectionElement) {
      // Avoid duplicate initialization
      if (this.instances.has(sectionElement)) {
        return this.instances.get(sectionElement);
      }

      const instance = new RnHeaderEnhancements(sectionElement);
      this.instances.set(sectionElement, instance);
      return instance;
    },

    destroy(sectionElement) {
      const instance = this.instances.get(sectionElement);
      if (instance) {
        instance.destroy();
        this.instances.delete(sectionElement);
      }
    },
  };
}

/**
 * Lifecycle hooks - Initialize on page load and section events
 */

// Helper function to initialize header
function initializeHeader() {
  const headerSection = document.querySelector('.shopify-section--header');
  if (headerSection) {
    return window.RnHeaderEnhancements.init(headerSection);
  }
}

// Initial load
document.addEventListener('DOMContentLoaded', () => initializeHeader());

// Shopify Theme Editor section lifecycle
document.addEventListener('shopify:section:load', (event) => {
  if (event.target.querySelector('[data-rn-header]')) {
    window.RnHeaderEnhancements.init(event.target);
  }
});

document.addEventListener('shopify:section:unload', (event) => {
  window.RnHeaderEnhancements.destroy(event.target);
});

// Page transition support - theme uses custom events on documentElement
document.documentElement.addEventListener('theme:loading:end', () => {
  initializeHeader();
});

document.addEventListener('theme:loading:end', () => {
  initializeHeader();
});

// Browser back/forward navigation
window.addEventListener('popstate', () => {
  setTimeout(() => initializeHeader(), 100);
});

// Health check - reinitialize if compression is enabled but instance is missing
let compressionCheckInterval = setInterval(() => {
  const headerSection = document.querySelector('.shopify-section--header');
  const header = headerSection?.querySelector('[data-rn-header]');

  if (header && (header.dataset.rnCompress === 'true' || header.dataset.rnReveal === 'true')) {
    const hasInstance = window.RnHeaderEnhancements.instances.has(headerSection);
    if (!hasInstance) {
      initializeHeader();
    }
  }
}, 2000);

// Stop health check after 30 seconds
setTimeout(() => clearInterval(compressionCheckInterval), 30000);

/* END-RN-HEADER-ENHANCEMENTS */
